import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookOpen, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import usePermissions from '../../hooks/usePermissions';
import { APP_NAV } from './modules';

/**
 * Left navigation panel. Lists every module with an icon, a title and a
 * subtitle, plus the "Ink it Admin" brand at the top. Items behind a permission
 * are hidden when the user cannot access them (same rule the API enforces).
 *
 * Entries can be grouped (`type: 'group'`): the group shows as a single item
 * that unfolds a submenu reusing the same item design. The group open when it
 * holds the active route, and can be collapsed/expanded by hand.
 */
export function Sidebar({ open = false, onNavigate }) {
    const location = useLocation();
    const { can, internal } = usePermissions();

    const canSee = (m) => (!m.permission || can(m.permission[0], m.permission[1]))
        && (!m.internalOnly || internal);

    const isActive = (m) => {
        if (location.pathname === m.path) return true;
        return (m.match || []).includes(location.pathname);
    };

    const [openGroups, setOpenGroups] = useState(() => {
        const initial = {};
        APP_NAV.forEach((entry) => {
            if (entry.type === 'group' && entry.children.some(isActive)) {
                initial[entry.key] = true;
            }
        });
        return initial;
    });

    // Keep the group of the active route unfolded when navigating (e.g. from a
    // link inside a module), without touching the ones the user opened by hand.
    useEffect(() => {
        APP_NAV.forEach((entry) => {
            if (entry.type === 'group' && entry.children.some(isActive)) {
                setOpenGroups((prev) => (prev[entry.key] ? prev : { ...prev, [entry.key]: true }));
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const toggleGroup = (key) => setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));

    const renderModule = (m, isChild = false) => (
        <Link
            key={m.key}
            to={m.path}
            onClick={() => {
                // Al elegir un módulo de primer nivel se pliega cualquier submenú
                // que haya quedado abierto; los hijos no lo cierran.
                if (!isChild) setOpenGroups({});
                if (onNavigate) onNavigate();
            }}
            data-tooltip={m.title}
            aria-label={m.title}
            className={`app-nav-item ${isActive(m) ? 'active' : ''}`}
        >
            <span className="app-nav-icon">
                <FontAwesomeIcon icon={m.icon} />
            </span>
            <span className="app-nav-text">
                <span className="app-nav-title">{m.title}</span>
                <span className="app-nav-subtitle">{m.subtitle}</span>
            </span>
        </Link>
    );

    return (
        <aside className={`app-sidebar ${open ? 'is-open' : ''}`}>
            <div className="app-sidebar-brand">
                <span className="app-brand-mark">
                    <FontAwesomeIcon icon={faBookOpen} />
                </span>
                <span className="app-brand-name">
                    Ink it <span className="app-brand-accent">Admin</span>
                </span>
            </div>

            <nav className="app-nav" aria-label="Módulos">
                {APP_NAV.map((entry) => {
                    if (entry.type === 'group') {
                        const children = entry.children.filter(canSee);
                        if (!children.length) return null;
                        const groupActive = children.some(isActive);
                        const expanded = !!openGroups[entry.key];
                        return (
                            <div key={entry.key} className="app-nav-group">
                                <button
                                    type="button"
                                    onClick={() => toggleGroup(entry.key)}
                                    data-tooltip={entry.title}
                                    aria-label={entry.title}
                                    aria-expanded={expanded}
                                    className={`app-nav-item app-nav-group-toggle ${groupActive ? 'active' : ''}`}
                                >
                                    <span className="app-nav-icon">
                                        <FontAwesomeIcon icon={entry.icon} />
                                    </span>
                                    <span className="app-nav-text">
                                        <span className="app-nav-title">{entry.title}</span>
                                        <span className="app-nav-subtitle">{entry.subtitle}</span>
                                    </span>
                                    <span className="app-nav-chevron">
                                        <FontAwesomeIcon icon={faChevronDown} />
                                    </span>
                                </button>
                                {expanded && (
                                    <div className="app-subnav">
                                        {children.map((c) => renderModule(c, true))}
                                    </div>
                                )}
                            </div>
                        );
                    }
                    return canSee(entry) ? renderModule(entry) : null;
                })}
            </nav>
        </aside>
    );
}

export default Sidebar;
